import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { injectCssVars } from '@aseller/antd-theme/css';
import { BrowserRouter, MemoryRouter, Routes, Route } from 'react-router-dom';
import { ConfigProvider, App as AntApp, Spin } from 'antd';
import type { ThemeConfig } from 'antd';
import type { Locale } from 'antd/es/locale';
import enUS from 'antd/locale/en_US';
import ruRU from 'antd/locale/ru_RU';
import ukUA from 'antd/locale/uk_UA';
import { I18nextProvider, useTranslation } from 'react-i18next';
import type { i18n as I18nInstance } from 'i18next';
import { appTheme, ThemeModeContext, ThemeToggleContext, STANDALONE_THEME_MODE, readStandaloneTheme, writeStandaloneTheme, type ThemeMode } from '../shared/theme';
import { createI18nInstance, getStandaloneI18n } from '../shared/i18n';
import { useAuthStore } from '../shared/session/authStore';
import LoginPage from '../pages/login/LoginPage';
import PlatformTokenGate from '../features/platform-token/PlatformTokenGate';
import PlatformSsoCallback from '../features/platform-sso/PlatformSsoCallback';
import { SSO_CALLBACK_PATH } from '../features/platform-sso/ssoFlow';
import DashboardView from '../pages/dashboard/DashboardView';
import SharedView from '../pages/shared-result/SharedView';
import { HostProvider, resolveEmbedLocale, type HostContext } from './contract';
import { readExchangeEntry, readProductEntry } from './hostEntry';
import { PlatformOriginContext } from '../shared/lib/platformOrigin';

// Map react-i18next language code → antd locale bundle.
// Embedded antd texts (Modal OK/Cancel, Empty "No data", pagination, form
// validation messages) follow this locale, so changes in i18next switch them too.
const ANTD_LOCALES: Record<string, Locale> = {
  en: enUS,
  ru: ruRU,
  uk: ukUA,
};

export function resolveAntdLocale(lang: string | undefined): Locale {
  // i18n.language может быть 'en-US' и т.п. — берём только базовый код.
  const base = (lang || 'ru').toLowerCase().split('-')[0];
  return ANTD_LOCALES[base] || enUS;
}

const DashboardRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuthStore();

  useEffect(() => {
    useAuthStore.getState().fetchMe();
  }, []);

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Гейт платформенного токена смонтирован ВЫШЕ развилки личности намеренно.
  // Он отвечает сразу за две двери: вход, когда личности ещё нет, и замену
  // отвергнутого токена, когда личность есть и держится как раз на нём. Внутри
  // ветки `!isAuthenticated` вторая дверь была бы недостижима по построению:
  // платформенный вход делает `isAuthenticated` истинным, и место, где чинят его
  // же токен, исчезало бы ровно после успешного входа. Ровно этим (плюс гардом
  // на обмене) happy path слоя и был недостижим в проде — MAJOR-1 ревью S2.
  return (
    <>
      <PlatformTokenGate />
      {isAuthenticated ? <DashboardView /> : <LoginPage />}
    </>
  );
};

const basePath = (import.meta.env.VITE_BASE_PATH || '/').replace(/\/+$/, '') || undefined;

const ROUTER_FUTURE = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

// antd по умолчанию портирует Tooltip/Popover/Modal в document.body. В
// standalone #root наш целиком, и всплывающие слои остаются внутри поддерева
// приложения, а не подмешиваются в body страницы. В embed этот контейнер
// намеренно НЕ задаётся — там #root принадлежит платформе (createEmbedMode).
const popupContainer = (): HTMLElement => document.getElementById('root') ?? document.body;

// Всё, что различается между режимами. Собирается один раз на идентичность
// контекста: host передаёт НОВЫЙ объект контекста повторным mount при смене
// темы, и пересоздание роутера на каждый рендер выкинуло бы пользователя с
// текущего экрана.
interface ModeConfig {
  i18n: I18nInstance;
  antdTheme: ThemeConfig;
  /** Тот же режим, что и у antdTheme: его читают графики, у которых свои темы. */
  themeMode: ThemeMode;
  getPopupContainer?: () => HTMLElement;
  routes: React.ReactNode;
}

// Двери входа существуют ТОЛЬКО в этой ветке — и это единственное место, где
// вопрос «какие двери показывать» решается. Ни `DashboardRoute` (а с ним ни
// форма логина, ни гейт платформенного токена), ни маршрут `/s/:shareId` в
// embed не монтируются вовсе: там личность даёт хост, и предлагать пользователю
// войти во второй раз значило бы предлагать сменить личность под платформой.
// Второго признака «мы в embed» рядом с этой развилкой заводить нельзя: в S2 из
// кода уже выпилили `IS_IN_IFRAME` ровно за то, что он был вторым, расходящимся
// ответом на тот же вопрос.
function createStandaloneMode(): ModeConfig {
  return {
    i18n: getStandaloneI18n(),
    antdTheme: appTheme(STANDALONE_THEME_MODE),
    themeMode: STANDALONE_THEME_MODE,
    getPopupContainer: popupContainer,
    routes: (
      <BrowserRouter basename={basePath} future={ROUTER_FUTURE}>
        <Routes>
          <Route path="/" element={<DashboardRoute />} />
          {/* Возврат с платформы после «Войти через Aseller». Отдельный маршрут, а не
              часть DashboardRoute: на нём сессии ЕЩЁ нет по определению, и гонять
              здесь проверку личности значило бы показывать форму логина поверх
              идущего обмена кода. */}
          <Route path={SSO_CALLBACK_PATH} element={<PlatformSsoCallback />} />
          <Route path="/s/:shareId" element={<SharedView />} />
        </Routes>
      </BrowserRouter>
    ),
  };
}

function createEmbedMode(context: HostContext): ModeConfig {
  // Товар карточки, с которой платформа открыла модуль. `null` и когда `entry`
  // не передан, и когда payload не содержит узнаваемого id — DashboardView
  // отличать эти случаи не обязан, только знать «подставлять есть что или нет».
  const productEntry = readProductEntry(context.entry);
  const exchangeXid = readExchangeEntry(context.entry);
  return {
    // Язык — из контекста, без localStorage: в embed им распоряжается платформа.
    i18n: createI18nInstance(resolveEmbedLocale(context.language)),
    // Режим целиком задаёт платформа: union HostTheme совпадает с ThemeMode
    // дизайн-системы, поэтому это присваивание, а не конверсия.
    antdTheme: appTheme(context.theme),
    themeMode: context.theme,
    // getPopupContainer не задаём: #root принадлежит платформе, а привязка
    // порталов к нашему поддереву существовала только ради авторесайза iframe.
    routes: (
      <MemoryRouter future={ROUTER_FUTURE}>
        <Routes>
          {/* Свой Header не рендерится — хром даёт платформа (спека §3.1). */}
          <Route path="/" element={<DashboardView withHeader={false} productEntry={productEntry} exchangeXid={exchangeXid} />} />
        </Routes>
      </MemoryRouter>
    ),
  };
}

interface AppShellProps {
  antdTheme: ThemeConfig;
  themeMode: ThemeMode;
  getPopupContainer?: () => HTMLElement;
  children: React.ReactNode;
}

// Отдельный компонент, потому что antd-локаль обязана следовать за языком в
// рантайме: useTranslation читает инстанс из I18nextProvider, а провайдер живёт
// уровнем выше.
const AppShell: React.FC<AppShellProps> = ({ antdTheme, themeMode, getPopupContainer, children }) => {
  const { i18n } = useTranslation();

  return (
    <ConfigProvider
      theme={antdTheme}
      locale={resolveAntdLocale(i18n.language)}
      getPopupContainer={getPopupContainer}
    >
      <ThemeModeContext.Provider value={themeMode}>
        <AntApp>{children}</AntApp>
      </ThemeModeContext.Provider>
    </ConfigProvider>
  );
};

export interface AppProps {
  // Контекст есть → embed под платформой; нет → standalone.
  context?: HostContext;
}

const App: React.FC<AppProps> = ({ context }) => {
  const mode = useMemo(
    () => (context ? createEmbedMode(context) : createStandaloneMode()),
    [context],
  );

  // Тему standalone выбирает зритель (WS 15587410, п.9); в embed её задаёт платформа, и
  // переключателя там нет: он приходит контекстом, который в embed пуст.
  const [standaloneTheme, setStandaloneTheme] = useState<ThemeMode>(readStandaloneTheme);
  const toggleTheme = useCallback(() => {
    setStandaloneTheme((prev) => {
      const next: ThemeMode = prev === 'dark' ? 'light' : 'dark';
      writeStandaloneTheme(next);
      // Переменные `--as-*` для глобальных стилей standalone (шапка, баннер версии) —
      // в documentElement; в embed сюда не попадаем, :root платформы не трогаем.
      injectCssVars(next);
      return next;
    });
  }, []);
  const themeMode = context ? mode.themeMode : standaloneTheme;
  const antdTheme = useMemo(
    () => (context ? mode.antdTheme : appTheme(standaloneTheme)),
    [context, mode, standaloneTheme],
  );

  const shell = (
    <I18nextProvider i18n={mode.i18n}>
      <ThemeToggleContext.Provider value={context ? null : toggleTheme}>
        <AppShell antdTheme={antdTheme} themeMode={themeMode} getPopupContainer={mode.getPopupContainer}>
          {mode.routes}
        </AppShell>
      </ThemeToggleContext.Provider>
    </I18nextProvider>
  );

  // HostProvider только в embed: useHost() обязан падать в standalone, а не
  // отдавать пустую личность. Там же — origin страницы платформы для ссылок в её разделы.
  return context ? (
    <HostProvider context={context}>
      <PlatformOriginContext.Provider value={window.location.origin}>{shell}</PlatformOriginContext.Provider>
    </HostProvider>
  ) : (
    shell
  );
};

export default App;
